import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { FormikProvider, useFormik } from 'formik';
import { queryCache, useMutation } from 'react-query';
import { QKeys } from 'src/api/types';
import * as Yup from 'yup';
import { Poppins, Spacer } from 'src/common';
import { Guard } from '../Guard';
import { Modal, ModalSubTitle } from '../comps';
import TabsMenu from '../../TabsMenu';
import { FromCsv, FromLib } from './forms';
import { addTemplatesToAssessment } from 'src/api/scenarioTemplates';
import { motion } from 'framer-motion';
import ScenarioForm from 'src/components/formComposed/ScenarioForm';
import { scenarioInitialValues, scenarioValidationSchema } from 'src/components/formComposed/ScenarioForm/util';
import { selectUserOption } from 'src/utils/misc';
import { useAuth } from 'src/state/auth';
import { showSuccessToast, toastMessages } from 'src/components/Toast';
import { mpEvent } from 'src/utils/mixpanel/useMixPanel';
import { MPEvents } from 'src/utils/mixpanel/types';
import { KeyType, useCache } from 'src/graphql/useCache';
import { ScenarioInput, useUpsertScenarioMutation } from 'src/graphql/_generated';
import { RiskIntelTab } from 'src/components/riskIntel';
import { AiSvg } from 'src/assets/dynamicSvg';

const StyledModal = styled(Modal)`
  width: 1568px;
  min-height: 200px;
  padding: 0 !important;
  overflow: hidden;

  .content {
    overflow-y: auto;
    max-height: calc(100vh - 40px);
  }
`;

const schemaLib = Yup.object({
  selected: Yup.array().of(Yup.string()).required('Required'),
});

interface AddRiskScenarioModalProps {
  assessmentId: string;
  assessmentName: string;
  onClose: () => void;
  existingScenarioIds: string[];
  invalidateQueries: KeyType[];
}

export const AddRiskScenarioModal: React.FC<AddRiskScenarioModalProps> = ({
  assessmentId,
  assessmentName,
  onClose,
  existingScenarioIds,
  invalidateQueries,
}) => {
  const [isCloseRequested, setIsCloseRequested] = useState(false);
  const [activeTab, setActiveTab] = useState<'from_new' | 'from_lib' | 'from_csv' | 'from_ai'>('from_new');
  // Once the AI tab is opened it stays MOUNTED (just hidden) for the rest of the modal's life, so an
  // in-progress AI session — provider state + websocket — survives switching to another tab and back.
  const [aiVisited, setAiVisited] = useState(false);
  useEffect(() => {
    if (activeTab === 'from_ai') setAiVisited(true);
  }, [activeTab]);
  const [err, setErr] = useState('');
  const { usersOptios } = useAuth();
  const [isScvDirty, setIsScvDirty] = useState(false);
  const [isCsvUploading, setIsCsvUploading] = useState(false);
  const { refetchQuery } = useCache();

  const onError = (err: any) => setErr(err?.message || 'Something went wrong');

  const [addFromLib] = useMutation(addTemplatesToAssessment, { onError });
  const [upsertScenario] = useUpsertScenarioMutation({ onError });

  const afterSuccess = async (count = 1) => {
    queryCache.invalidateQueries([QKeys.Audit]);
    await refetchQuery({ include: invalidateQueries });
    showSuccessToast(count === 1 ? toastMessages.scenario.created : toastMessages.scenario.createdPlural);
    onClose();
  };

  const formikNew = useFormik({
    initialValues: scenarioInitialValues,
    validationSchema: scenarioValidationSchema,

    onSubmit: async (values) => {
      setErr('');
      const lowerFormula = String(values.lowerFormula).replace(/\s+/g, '');
      const upperFormula = String(values.upperFormula).replace(/\s+/g, '');

      const { userAdd } = selectUserOption({
        usersOptios,
        selectedUser: values.riskOwner,
      });

      const result = await upsertScenario({
        variables: {
          assessmentId,
          scenarioData: {
            ...values,
            riskOwner: userAdd,
            lowerFormula,
            upperFormula,
          },
        },
      });
      mpEvent(MPEvents.ScenarioCreate, { id: result?.data?.upsertScenario?.id!, tags: ['SCENARIO'] });
      await afterSuccess();
    },
  });

  // The AI "Create with AI" review owns its own assembled ScenarioInput (it doesn't drive
  // formikNew), so it gets a dedicated create handler that reuses the same upsert + afterSuccess
  // machinery. Returns false on error (the review stays open and `err` is shown), true on success
  // (afterSuccess closes the modal + toasts) — see RiskIntelTab onCreateScenario.
  const createFromAi = async (data: Partial<ScenarioInput>): Promise<boolean> => {
    setErr('');
    const lowerFormula = String(data.lowerFormula ?? '').replace(/\s+/g, '');
    const upperFormula = String(data.upperFormula ?? '').replace(/\s+/g, '');
    const result = await upsertScenario({
      variables: { assessmentId, scenarioData: { ...data, lowerFormula, upperFormula } },
    });
    const id = result?.data?.upsertScenario?.id;
    if (!id) return false; // onError already populated `err`
    mpEvent(MPEvents.ScenarioCreate, { id, tags: ['SCENARIO', 'AI'] });
    await afterSuccess();
    return true;
  };

  const formikLib = useFormik({
    initialValues: {
      selected: [],
    },
    validationSchema: schemaLib,
    onSubmit: async (values) => {
      setErr('');
      await addFromLib({ assessments: [assessmentId], templates: values.selected });
      mpEvent(MPEvents.ScenariosImportedFromLibrary, { templates: values.selected, tags: ['SCENARIO'] });
      await afterSuccess(values.selected.length);
    },
  });

  return (
    <Guard
      onCancel={() => setIsCloseRequested(false)}
      onClose={() => onClose()}
      isOpen={isCloseRequested}
      isDirty={formikLib.dirty || formikNew.dirty || isScvDirty}
      modalComponent={
        <StyledModal onRequestClose={() => setIsCloseRequested(true)} isOpen center={false} persistent={formikNew.isSubmitting || formikLib.isSubmitting || isCsvUploading}>
          <div className="content">
            <Spacer $px={40} />
            <div className="h-padding">
              <Poppins className="m-title" px={28}>
                Add Risk Scenario
              </Poppins>
              <ModalSubTitle title="ASSESSMENT" value={assessmentName} />
              <Spacer $px={40} />
              <TabsMenu
                tabs={[
                  { label: 'Create manually', value: 'from_new', dataCy: 'from_new' },
                  {
                    // Always visible — the gate (locked/upsell vs enabled) is decided inside the
                    // tab by useRiskIntelGate, per the design (screen-01) and the req doc's "everyone
                    // should have a disabled trigger" (ticket 7127).
                    label: (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <AiSvg isActive style={{ width: 16, height: 16 }} />
                        Create with AI
                      </span>
                    ),
                    value: 'from_ai',
                    dataCy: 'from_ai',
                  },
                  { label: 'Import From Library', value: 'from_lib', dataCy: 'from_lib' },
                  { label: 'Import from CSV', value: 'from_csv', dataCy: 'from_csv' },
                ]}
                activeTab={activeTab}
                setActiveTab={(tab) => {
                  setActiveTab(tab);
                  mpEvent(MPEvents.ButtonClick, {
                    button:
                      tab === 'from_new'
                        ? 'Tab:Enter Risk Details'
                        : tab === 'from_lib'
                          ? 'Tab:Import From Library'
                          : tab === 'from_csv'
                            ? 'Tab:Import from CSV'
                            : 'Tab:Create with AI',
                    modal: 'Scenario add modal',
                    tags: ['SCENARIO'],
                  });
                }}
              />
              <Spacer $px={45} />
            </div>
            {activeTab === 'from_new' && (
              <FormikProvider value={formikNew}>
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0, duration: 0.1 }}>
                  <ScenarioForm
                    buttonTitle="CREATE"
                    assessmentId={assessmentId}
                    cb={() =>
                      mpEvent(MPEvents.ButtonClick, {
                        button: 'Create',
                        modal: 'Scenario add modal',
                        tags: ['SCENARIO'],
                      })
                    }
                  />
                </motion.div>
              </FormikProvider>
            )}
            {activeTab === 'from_lib' && (
              <FormikProvider value={formikLib}>
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0, duration: 0.1 }}>
                  <FromLib />
                </motion.div>
              </FormikProvider>
            )}
            {activeTab === 'from_csv' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0, duration: 0.1 }}>
                <FromCsv
                  assessmentId={assessmentId}
                  onClose={onClose}
                  setIsScvDirty={setIsScvDirty}
                  onUploadingChange={setIsCsvUploading}
                  existingScenarioIds={existingScenarioIds}
                  invalidateQueries={invalidateQueries}
                />
              </motion.div>
            )}
            {aiVisited && (
              // Mounted-but-hidden when inactive (not conditionally rendered) so the AI session survives tab switches.
              <div className="h-padding" style={{ display: activeTab === 'from_ai' ? undefined : 'none' }}>
                <RiskIntelTab
                  assessmentId={assessmentId}
                  assessmentName={assessmentName}
                  onCreateScenario={createFromAi}
                />
              </div>
            )}

            {!!err && (
              <div className="h-padding" css="text-align: center">
                <Spacer $px={15} />
                <Poppins px={14} color="error">
                  {err}
                </Poppins>
              </div>
            )}
            <Spacer $px={40} />
          </div>
        </StyledModal>
      }
    />
  );
};
